import { useCallback, useMemo, useRef, useState } from "react";

import { isEqual } from "lodash-es";

import {
  CellDisplayType,
  getAllKeys,
  MIN_STRING_CELL_HEIGHT,
} from "src/cases/caseView/blocks/cellTypes";
import { TableBlockMultiColumnRecord } from "src/cases/queries/casesTypes";

type CellKey = `${number}:${string}`;

const makeCellKey = (rowIndex: number, columnKey: string): CellKey =>
  `${rowIndex}:${columnKey}`;

const parseCellKey = (
  key: CellKey,
): { rowIndex: number; columnKey: string } => {
  const separatorIndex = key.indexOf(":");
  return {
    rowIndex: Number(key.slice(0, separatorIndex)),
    columnKey: key.slice(separatorIndex + 1),
  };
};

type RowOperation =
  | { type: "delete"; index: number }
  | { type: "duplicate"; index: number }
  | { type: "add" };

export type UseTableBlockEditsReturn = {
  editedData: TableBlockMultiColumnRecord;
  isCellModified: (rowIndex: number, columnKey: string) => boolean;
  hasChanges: boolean;
  updateCell: (rowIndex: number, columnKey: string, value: unknown) => void;
  addRow: () => void;
  deleteRow: (rowIndex: number) => void;
  duplicateRow: (rowIndex: number) => void;
  resetEdits: () => void;
  cellHeights: ReadonlyMap<CellKey, number>;
  setCellHeight: (rowIndex: number, columnKey: string, height: number) => void;
  getCellHeight: (rowIndex: number, columnKey: string) => number | undefined;
  restoreCellHeights: (heights: ReadonlyMap<CellKey, number>) => void;
  setTypeOverride: (
    rowIndex: number,
    columnKey: string,
    displayType: CellDisplayType | null,
  ) => void;
  getTypeOverride: (
    rowIndex: number,
    columnKey: string,
  ) => CellDisplayType | undefined;
  isNewRow: (rowIndex: number) => boolean;
};

export const useTableBlockEdits = (
  originalData: TableBlockMultiColumnRecord,
): UseTableBlockEditsReturn => {
  // null = no local row changes, derive workingData from originalData directly.
  // This keeps data in sync with polling when the user isn't actively editing.
  const [localData, setLocalData] =
    useState<TableBlockMultiColumnRecord | null>(null);
  const [cellEdits, setCellEdits] = useState<Map<CellKey, unknown>>(new Map());
  const [typeOverrides, setTypeOverrides] = useState<
    Map<CellKey, CellDisplayType>
  >(new Map());
  const [newRowIndices, setNewRowIndices] = useState<Set<number>>(new Set());
  // User-dragged string cell heights. Not part of the saved payload, so they
  // survive resetEdits — the read-only view renders them after saving.
  const [cellHeights, setCellHeights] = useState<Map<CellKey, number>>(
    new Map(),
  );

  const workingData = localData ?? originalData;

  const originalDataRef = useRef(originalData);
  originalDataRef.current = originalData;

  const editsByRow = useMemo(
    () =>
      [...cellEdits.entries()].reduce<Map<number, Record<string, unknown>>>(
        (grouped, [key, value]) => {
          const { rowIndex, columnKey } = parseCellKey(key);
          const existing = grouped.get(rowIndex);
          if (existing) {
            existing[columnKey] = value;
          } else {
            grouped.set(rowIndex, { [columnKey]: value });
          }
          return grouped;
        },
        new Map<number, Record<string, unknown>>(),
      ),
    [cellEdits],
  );

  const editedData = useMemo(
    () =>
      workingData.map((row, rowIndex) => {
        const rowEdits = editsByRow.get(rowIndex);
        return rowEdits ? { ...row, ...rowEdits } : row;
      }),
    [workingData, editsByRow],
  );

  const isCellModified = useCallback(
    (rowIndex: number, columnKey: string): boolean =>
      cellEdits.has(makeCellKey(rowIndex, columnKey)),
    [cellEdits],
  );

  // Deep-compare so reverted edits and cancelled-out row ops disable Save.
  const hasChanges = useMemo(
    () => !isEqual(editedData, originalData),
    [editedData, originalData],
  );

  const workingDataRef = useRef(workingData);
  workingDataRef.current = workingData;

  const updateCell = useCallback(
    (rowIndex: number, columnKey: string, value: unknown) => {
      const key = makeCellKey(rowIndex, columnKey);
      const originalValue = workingDataRef.current[rowIndex]?.[columnKey];
      if (isEqual(value, originalValue)) {
        setCellEdits((prev) => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
      } else {
        // Snapshot data on first real edit so polling can't shift row indices
        setLocalData((prev) => prev ?? originalDataRef.current);
        setCellEdits((prev) => {
          const next = new Map(prev);
          next.set(key, value);
          return next;
        });
      }
    },
    [],
  );

  const applyRowOperation = useCallback((op: RowOperation) => {
    setLocalData((prev) => {
      const current = prev ?? originalDataRef.current;
      switch (op.type) {
        case "delete":
          return current.filter((_, i) => i !== op.index);
        case "duplicate": {
          const rowCopy = { ...current[op.index] };
          return [
            ...current.slice(0, op.index + 1),
            rowCopy,
            ...current.slice(op.index + 1),
          ];
        }
        case "add": {
          const keys = getAllKeys(current);
          const emptyRow = Object.fromEntries(keys.map((k) => [k, null]));
          return [...current, emptyRow];
        }
      }
    });

    const shiftKeyedMap = <V>(prev: Map<CellKey, V>): Map<CellKey, V> =>
      [...prev.entries()].reduce<Map<CellKey, V>>((next, [key, value]) => {
        const { rowIndex, columnKey } = parseCellKey(key);

        if (op.type === "delete") {
          if (rowIndex === op.index) return next;
          const shifted = rowIndex > op.index ? rowIndex - 1 : rowIndex;
          next.set(makeCellKey(shifted, columnKey), value);
        } else if (op.type === "duplicate") {
          const shifted = rowIndex > op.index ? rowIndex + 1 : rowIndex;
          next.set(makeCellKey(shifted, columnKey), value);
          if (rowIndex === op.index) {
            next.set(makeCellKey(op.index + 1, columnKey), value);
          }
        } else {
          next.set(key, value);
        }

        return next;
      }, new Map<CellKey, V>());

    setCellEdits(shiftKeyedMap);
    setTypeOverrides(shiftKeyedMap);
    setCellHeights(shiftKeyedMap);

    setNewRowIndices((prev) => {
      switch (op.type) {
        case "delete":
          return new Set(
            [...prev]
              .filter((idx) => idx !== op.index)
              .map((idx) => (idx > op.index ? idx - 1 : idx)),
          );
        case "duplicate":
          return new Set([
            ...[...prev].map((idx) => (idx > op.index ? idx + 1 : idx)),
            op.index + 1,
          ]);
        case "add":
          // workingDataRef still holds the pre-add data (state hasn't
          // re-rendered yet), so its length equals the new row's index.
          return new Set([...prev, workingDataRef.current.length]);
      }
    });
  }, []);

  const addRow = useCallback(
    () => applyRowOperation({ type: "add" }),
    [applyRowOperation],
  );

  const deleteRow = useCallback(
    (rowIndex: number) =>
      applyRowOperation({ type: "delete", index: rowIndex }),
    [applyRowOperation],
  );

  const duplicateRow = useCallback(
    (rowIndex: number) =>
      applyRowOperation({ type: "duplicate", index: rowIndex }),
    [applyRowOperation],
  );

  const resetEdits = useCallback(() => {
    setLocalData(null);
    setCellEdits(new Map());
    setTypeOverrides(new Map());
    setNewRowIndices(new Set());
  }, []);

  const setTypeOverride = useCallback(
    (
      rowIndex: number,
      columnKey: string,
      displayType: CellDisplayType | null,
    ) => {
      const key = makeCellKey(rowIndex, columnKey);
      setTypeOverrides((prev) => {
        const next = new Map(prev);
        if (displayType === null) {
          next.delete(key);
        } else {
          next.set(key, displayType);
        }
        return next;
      });
    },
    [],
  );

  const getTypeOverride = useCallback(
    (rowIndex: number, columnKey: string): CellDisplayType | undefined =>
      typeOverrides.get(makeCellKey(rowIndex, columnKey)),
    [typeOverrides],
  );

  const isNewRow = useCallback(
    (rowIndex: number): boolean => newRowIndices.has(rowIndex),
    [newRowIndices],
  );

  const setCellHeight = useCallback(
    (rowIndex: number, columnKey: string, height: number) => {
      setCellHeights((prev) => {
        const key = makeCellKey(rowIndex, columnKey);
        // A single-line height means default rendering — drop the entry.
        if (height <= MIN_STRING_CELL_HEIGHT) {
          if (!prev.has(key)) return prev;
          const next = new Map(prev);
          next.delete(key);
          return next;
        }
        if (prev.get(key) === height) return prev;
        return new Map(prev).set(key, height);
      });
    },
    [],
  );

  const getCellHeight = useCallback(
    (rowIndex: number, columnKey: string): number | undefined =>
      cellHeights.get(makeCellKey(rowIndex, columnKey)),
    [cellHeights],
  );

  const restoreCellHeights = useCallback(
    (heights: ReadonlyMap<CellKey, number>) => {
      setCellHeights(new Map(heights));
    },
    [],
  );

  return {
    editedData,
    isCellModified,
    hasChanges,
    updateCell,
    addRow,
    deleteRow,
    duplicateRow,
    resetEdits,
    cellHeights,
    setCellHeight,
    getCellHeight,
    restoreCellHeights,
    setTypeOverride,
    getTypeOverride,
    isNewRow,
  };
};
